import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useGetRepairRequestQuery } from "../../slices/maintenanceApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Eye,
  MapPin,
  User,
  Mail,
  Phone,
  Clock,
  AlertCircle,
  Bookmark,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

const AdminRepair = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("");
    }
  }, [searchParams]);
  const [selectedLocation, setSelectedLocation] = useState("");

  const [selectedRepair, setSelectedRepair] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const rowsPerPage = 10;
  const {
    data: repairRequest,
    isLoading,
    refetch,
  } = useGetRepairRequestQuery({
    pollingInterval: 3000,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Use Sonner for loading state
  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading data, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoading]);

  const { data: academy } = useGetAcademyQuery();

  const handleOpenViewModal = (repair) => {
    setSelectedRepair(repair);
    setIsViewModalOpen(true);
  };

  const [data, setData] = useState([]);

  useEffect(() => {
    if (!repairRequest) return;
    // Ensure we are working with a copy to avoid mutation errors if RTK Query data is frozen
    const sorted = [...repairRequest]
      .sort(
        (a, b) =>
          new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime(),
      )
      .reverse();
    setData(sorted);
  }, [repairRequest]);

  const getAcademyName = (academyID) => {
    const match = academy?.find((a) => a.academyId === academyID);
    return match ? match.name : "Unknown";
  };

  // Filtering data based on search and priority selection and work status
  const sortedData = [...data];
  const filteredData = sortedData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();

    const matchesSearch =
      item.repairID?.toString().toLowerCase().includes(searchLower) ||
      Object.values(item).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchLower),
      );

    const matchesPriority =
      selectedPriority === "" ||
      item.priority?.toLowerCase() === selectedPriority.toLowerCase();

    const matchesWorkStatus =
      selectedWorkStatus === "" ||
      item.status?.toLowerCase() === selectedWorkStatus.toLowerCase();

    const matchesLocation =
      selectedLocation === "" ||
      getAcademyName(item.academyId)?.toLowerCase() ===
      selectedLocation.toLowerCase();

    return (
      matchesSearch && matchesPriority && matchesWorkStatus && matchesLocation
    );
  });

  console.log("Filtered Data:", filteredData);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Repair Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and track repair maintenance requests and work orders
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search repairs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="flex h-10 w-full md:w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">All Priorities</option>
            {Array.from(
              new Set(
                data
                  .map((item) => item.priority?.toLowerCase())
                  .filter(Boolean),
              ),
            ).map((priority) => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>



          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="flex h-10 w-full md:w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">All Locations</option>
            {Array.from(
              new Set(
                data.map((item) =>
                  getAcademyName(item.academyId)?.toLowerCase(),
                ),
              ),
            ).map((location) => (
              <option key={location} value={location}>
                {location.charAt(0).toUpperCase() + location.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Image</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">AssetName</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Priority</TableHead>
                <TableHead className="font-semibold">Work Status</TableHead>
                <TableHead className="font-semibold text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow key={item.repairID || index}>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {item.images && item.images[0] ? (
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={item.images[0]} alt={item.name} />
                          <AvatarFallback>
                            {item.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>
                            {item.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.email}
                    </TableCell>
                    <TableCell>{item.phoneNumber}</TableCell>
                    <TableCell>{item.assetName}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.area}</TableCell>
                    <TableCell>{getAcademyName(item.academyId)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          item.priority?.toLowerCase() === "immediate"
                            ? "destructive"
                            : item.priority?.toLowerCase() === "high"
                              ? "warning"
                              : item.priority?.toLowerCase() === "medium"
                                ? "secondary"
                                : "outline"
                        }
                      >
                        {item.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.status?.toLowerCase() === "rejected" || item.accept === false ? (
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                          Rejected
                        </Badge>
                      ) : (
                        <Badge
                          variant={
                            item.status?.toLowerCase() === "completed"
                              ? "success"
                              : item.status?.toLowerCase() === "inprogress"
                                ? "warning"
                                : "secondary"
                          }
                        >
                          {item.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenViewModal(item)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No repair requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              {filteredData.length} Results
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-background">
            <DialogHeader className="px-6 py-4 border-b bg-muted/40">
              <DialogTitle className="text-xl flex items-center gap-3 pr-8">
                <span>Repair Request : {selectedRepair?.repairCode}</span>
                {selectedRepair && (
                  <Badge
                    variant={
                      selectedRepair.status?.toLowerCase() === "completed"
                        ? "success"
                        : "secondary"
                    }
                  >
                    {selectedRepair.status}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-sm font-medium">
                Submitted on{" "}
                {selectedRepair?.submissionDate
                  ? new Date(selectedRepair.submissionDate).toLocaleString()
                  : "N/A"}
              </DialogDescription>
            </DialogHeader>

            {selectedRepair && (
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-6">
                {/* User Context */}
                <div className="flex items-center gap-4 bg-muted/30 p-4 rounded-lg border">
                  <Avatar className="h-12 w-12 border">
                    {selectedRepair.images && selectedRepair.images[0] ? (
                      <AvatarImage
                        src={selectedRepair.images[0]}
                        alt={selectedRepair.name}
                      />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                      {selectedRepair.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid gap-1 flex-1">
                    <div className="font-semibold text-base flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {selectedRepair.name}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-4">
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        {selectedRepair.email || "N/A"}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        {selectedRepair.phoneNumber || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                      <MapPin className="h-4 w-4" /> Location
                    </div>
                    <p className="font-medium pl-5.5">
                      {getAcademyName(selectedRepair.academyId)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                      <Bookmark className="h-4 w-4" /> Area
                    </div>
                    <p className="font-medium pl-5.5">
                      {selectedRepair.area || "N/A"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                      <AlertCircle className="h-4 w-4" /> Priority
                    </div>
                    <div className="pl-5.5">
                      <Badge
                        variant={
                          selectedRepair.priority?.toLowerCase() === "immediate"
                            ? "destructive"
                            : selectedRepair.priority?.toLowerCase() === "high"
                              ? "warning"
                              : selectedRepair.priority?.toLowerCase() === "medium"
                                ? "secondary"
                                : "outline"
                        }
                      >
                        {selectedRepair.priority}
                      </Badge>
                    </div>
                  </div>
                  {/* 
                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                      <FileText className="h-4 w-4" /> Issue Type
                    </div>
                    <p className="font-medium pl-5.5">
                      {selectedRepair.priority || "General Maintenance"}
                    </p>
                  </div> */}
                </div>

                <Separator />

                {/* Description */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm flex items-center gap-1.5">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Detailed Description
                  </h4>
                  <div className="bg-muted/40 p-4 rounded-lg text-sm leading-relaxed border shadow-sm">
                    {selectedRepair.description ||
                      "No specific details provided by the requester."}
                  </div>
                </div>

                {/* Images */}
                {selectedRepair.images && selectedRepair.images.length > 0 && (
                  <div className="space-y-3 pb-2">
                    <h4 className="font-semibold text-sm flex items-center gap-1.5">
                      Attached Evidence ({selectedRepair.images.length})
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedRepair.images.map((img, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-square rounded-md overflow-hidden border shadow-sm group"
                        >
                          <img
                            src={img}
                            alt={`Attachment ${idx + 1}`}
                            className="object-cover w-full h-full transition-transform group-hover:scale-105"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminRepair;
